import React, { FC, useRef, useContext } from 'react';
import { ContextApi } from 'react-uforms';
import { WithStyle } from '@ch/common/utils';
import { FormFileProps } from './types';
import { FormFileNameWrapper, FormFileStyled } from './file.styled';
import { Button } from '@ch/common/components/button';
import { IconDelete, IconUploadDocument } from '@ch/common/components/icon';
import { Box } from '@ch/common/components/box';

export const FormFile: FC<FormFileProps> & WithStyle = ({
  onClear,
  onChange,
  img,
  onUpload,
  hideControls = false,
  nofileChosenText,
  ...props
}) => {
  const api = useContext(ContextApi);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleClick = () => {
    inputRef?.current?.click();
  };
  const handleChange = (e: any) => {
    e.persist();
    onChange?.(e);
  };
  const handleClear = () => {
    api?.setValue(props.name, null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onClear?.(props.name);
  };
  return (
    <>
      <Box $display="flex" $alignItems="center" $justifyContent="center" $m={[0, 0, 15, 0]}>
        {img ? (
          <Box $mr={15}>
            <img src={img} height={100} style={{ maxWidth: 200, objectFit: 'contain' }} />
            <FormFileStyled type="file" {...props} onChange={handleChange} ref={inputRef} hidden />
          </Box>
        ) : (
          <>
            <FormFileNameWrapper
              title={inputRef?.current?.value ? inputRef?.current?.files?.item(0)?.name : nofileChosenText}
              hideControls={onUpload && inputRef?.current?.value ? true : false}
            >
              {inputRef?.current?.value ? inputRef?.current?.files?.item(0)?.name : nofileChosenText}
            </FormFileNameWrapper>
            <FormFileStyled
              type="file"
              title={inputRef?.current?.value ? inputRef?.current?.files?.item(0)?.name : nofileChosenText}
              {...props}
              onChange={handleChange}
              ref={inputRef}
              hideControls={onUpload && inputRef?.current?.value ? true : false}
              translatedText={nofileChosenText}
              hidden
            />
            {props.clearable && inputRef?.current?.value && (
              <Button
                type="button"
                $variant="icon"
                $color="primary"
                $size="ico-xs"
                onClick={handleClear}
                title={props?.removeButtonTitle}
                $m={[0, 0, 0, 30]}
              >
                <IconDelete $size={14} $color="primary" />
              </Button>
            )}
          </>
        )}
      </Box>
      <Box $display="flex" $alignItems="center" $justifyContent="center">
        {!hideControls && (
          <>
            {onUpload && inputRef?.current?.value ? (
              <Button $size="sm" $variant="outlined" onClick={() => onUpload(api)}>
                <IconUploadDocument $size={18} $color="primary" $m={[0, 10, 0, 0]} />
                {props.uploadButtonTitle}
              </Button>
            ) : (
              <Button
                type="button"
                $color={props.buttonStyle}
                $variant="outlined"
                disabled={props.disabled}
                $size="sm"
                onClick={handleClick}
              >
                <IconUploadDocument $size={18} $color="primary" $m={[0, 10, 0, 0]} />
                {props.selectButtonTitle}
              </Button>
            )}
          </>
        )}
      </Box>
    </>
  );
};

FormFile.defaultProps = {
  buttonStyle: 'primary',
  selectButtonTitle: 'Replace file',
  removeButtonTitle: 'Remove file',
  uploadButtonTitle: 'Upload',
  clearable: true,
};

FormFile.displayName = 'FormFile';
FormFile.Style = FormFileStyled;
